import React, { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';
import capitalizeFirst from '@zola-helpers/client/dist/es/transformers/capitalizeFirst';
import { ButtonColorway, ButtonSize, ButtonVariant } from './types';
import { ButtonWrapper, ButtonComponent } from './ButtonV3.styles';

export type ButtonV3Props = Partial<ButtonHTMLAttributes<HTMLButtonElement>> &
  Partial<AnchorHTMLAttributes<HTMLAnchorElement>> & {
    component?: ButtonComponent;
    variant?: ButtonVariant;
    /** Preapproved button colorways. Only available for primary and secondary variants. Only to be used on backgrounds matching the colorway. */
    colorway?: ButtonColorway;
    size?: ButtonSize;
    tabletSize?: ButtonSize;
    mobileSize?: ButtonSize;
    /** @deprecated - does not seemed to be used or set */
    bordered?: boolean;
    fullWidth?: boolean;
    textTransform?: boolean;
    className?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    innerRef?: React.RefObject<any>;
    /** @deprecated Use size='small' instead */
    compact?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  };

/**
 * ButtonV3 will render a UI element that looks like a button.  It can either be
 * an HTML button, or a link that looks like a button.
 *
 * ## Button Colors
 *
 * Buttons can be colored in two ways.  The first, and primary way to color buttons
 * is using the variant prop.  The variant prop can be set to `primary` and `secondary`
 * as the two main button styles used.  Primary indicates the main action we would
 * like the user to take, and if there is a secondary action, the secondary button
 * can be used.  For example: Submitting a form is a primary action, but cancelling
 * or closing a modal/form is a secondary action.  Primary and secondary buttons
 * can have a colorway applied, which will change the button colors from black/white
 * in registry (or blue/white in baby) to a color complimentary to a background.
 *
 * If a button color needs to be adjusted for a special case, we should use the
 * button css variables defined for each variant  This is how web-baby will color
 * buttons to be blue instead of black, and shared components that render in a
 * baby or a registry context will "just work".  Since CSS variables can be locally
 * scoped, besides color buttons for the baby/registry context, they can also be used
 * to override button colors in a local scope.  This mechanism for color button
 * replaces both using emotion and styled components, as well as using custom
 * color/background colors in less modules.
 *
 * @example - Old way to color buttons
 * ```
 * // Do not do this
 * const ColoredButton = styled(ButtonV3)`
 *   background-color: purple;
 *   color: gold
 * `;
 * ```
 *
 * @example - New way to color buttons
 * ```
 * const ColoredButton = styled(ButtonV3)`
 * --button-primary-foreground: gold;
 * --button-primary-background: purple;
 * --button-primary-hover-foreground: gold;
 * --button-primary-hover-background: violet;
 * `;
 * ```
 *
 * ### Button Variables
 * 
 * For the primary and secondary buttons, when a colorway is not used, the button
 * can be colored with a css variable.

 * ```
 * --button-primary-background
 * --button-primary-foreground
 * --button-primary-hover-background
 * --button-primary-hover-foreground
 * --button-primary-focus-background
 * --button-primary-focus-foreground
 * --button-primary-disabled-background
 * --button-primary-disabled-foreground
 * --button-primary-disabled-opacity
 *
 * --button-secondary-background
 * --button-secondary-foreground
 * --button-secondary-border
 * --button-secondary-hover-background
 * --button-secondary-hover-foreground
 * --button-secondary-focus-background
 * --button-secondary-focus-foreground
 * --button-secondary-disabled-background
 * --button-secondary-disabled-foreground
 * --button-secondary-disabled-border
 * ```
 *
 */
export const ButtonV3: React.FC<ButtonV3Props> = ({
  children,
  disabled,
  component: Component = 'button',
  variant = 'primary',
  bordered,
  compact,
  fullWidth,
  textTransform = true,
  className,
  onClick,
  rel,
  role = 'button',
  tabIndex,
  type = 'button',
  href,
  innerRef,
  target,
  colorway,
  size = 'medium',
  ...props
}) => {
  const elementProps = {
    onClick: !disabled && onClick ? onClick : undefined,
    className,
    href: Component === 'a' ? href : undefined,
    tabIndex: Component === 'button' ? tabIndex : undefined,
    variant,
    disabled,
    bordered,
    fullWidth,
    compact,
    type,
    role,
    target,
    rel,
    colorway,
    size,
    ...props,
  };

  if (disabled) elementProps.disabled = disabled;
  if (Component === 'button') {
    elementProps.type = type;
    elementProps.role = role;
    if (tabIndex === undefined && disabled) {
      elementProps.tabIndex = -1;
    }
  } else if (Component === 'a') {
    // @ts-expect-error links do not have this property
    delete elementProps.type;
    // @ts-expect-error links do not have this property
    delete elementProps.role;
    if (target !== '') {
      elementProps.target = target;
      if (target === '_blank') {
        elementProps.rel = `noopener noreferrer ${rel}`;
      }
    }
    if (!href) {
      elementProps.href = '#';
    }
  }
  if (compact) elementProps.size = 'small';

  const applySentenceCase = textTransform && typeof children === 'string';

  return (
    <ButtonWrapper as={Component} ref={innerRef} {...elementProps}>
      {applySentenceCase ? capitalizeFirst(String(children || ''), 1) : children}
    </ButtonWrapper>
  );
};

export default ButtonV3;
